import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, img, errMsg } from "../lib/api";

export const Card = ({ children, className = "", ...r }) => <div className={`bg-white rounded-3xl border border-[#f1e4ea] p-5 sm:p-6 ${className}`} {...r}>{children}</div>;

export const Title = ({ children, right }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
    <h1 className="text-3xl font-semibold">{children}</h1>
    {right}
  </div>
);

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-1">{label}</span>
      {children}
    </label>
  );
}

export function Toggle({ checked, onChange, testid, label }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid} className="inline-flex items-center gap-2 text-sm font-semibold">
      <span className={`w-10 h-6 rounded-full p-0.5 transition-colors ${checked ? "bg-[var(--teal)]" : "bg-gray-200"}`}>
        <span className={`block w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
      </span>
      {label}
    </button>
  );
}

export async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await adminApi.post("/upload-image", fd);
  return data.path;
}

export function ImageInput({ value, onChange, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      onChange(await uploadImage(f));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3">
      <img src={img(value)} alt="" className="w-16 h-16 rounded-2xl object-cover bg-[var(--blush)]" />
      <button type="button" onClick={() => ref.current.click()} className="btn-ghost !py-2 !px-4" data-testid={testid}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Nahrát</button>
      <input ref={ref} type="file" accept="image/*" hidden onChange={pick} data-testid={testid ? `${testid}-file` : undefined} />
      {value && <button type="button" onClick={() => onChange("")} className="text-xs font-bold text-[var(--muted)] hover:text-[var(--pink-dark)] underline" data-testid={testid ? `${testid}-clear` : undefined}>Odebrat</button>}
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/30 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" onMouseDown={onClose}>
      <div className={`bg-white rounded-3xl w-full ${wide ? "max-w-4xl" : "max-w-xl"} my-8 p-6 sm:p-8`} onMouseDown={(e) => e.stopPropagation()} data-testid="admin-modal">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">{title}</h2>
          <button onClick={onClose} data-testid="admin-modal-close" className="w-9 h-9 rounded-full hover:bg-[var(--blush)] grid place-items-center"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export const Badge = ({ children, tone = "pink" }) => {
  const t = { pink: "bg-[var(--pink-soft)] text-[var(--pink-dark)]", teal: "bg-[var(--teal-soft)] text-[#2b8f8a]", sun: "bg-[var(--sun-soft)] text-[#8a6d00]", gray: "bg-gray-100 text-gray-600" }[tone];
  return <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full ${t}`}>{children}</span>;
};

export const STATUS = { pending: ["Čeká na platbu", "sun"], paid: ["Zaplaceno", "teal"], cancelled: ["Zrušeno", "gray"] };

export async function act(fn, ok) {
  try {
    const r = await fn();
    window.dispatchEvent(new Event("site-changed"));
    if (ok) toast.success(ok);
    return r;
  } catch (e) {
    toast.error(errMsg(e));
    throw e;
  }
}
